import React from 'react';
import { AssistantProvider } from '@/features/assistant/context/AssistantContext';
import { Chatbot } from '@/features/assistant/components/Chatbot';
import { IAssistantConfig } from '@/features/assistant/types';

interface ConversationFlow {
  version?: string;
  chatbotConfig?: {
    greeting?: string;
    language?: string;
    currency?: string;
    units?: string;
    defaultRegion?: string;
  };
  questionFlow?: any[];
  questions?: any[];
  validationRules?: any[];
  pricingRules?: any[];
}

const toAssistantConfig = (flow: ConversationFlow): IAssistantConfig => ({
  version: flow.version || '1.0.0',
  configName: 'Admin Preview',
  lastUpdated: new Date().toISOString(),
  chatbotConfig: {
    greeting: flow.chatbotConfig?.greeting,
    language: flow.chatbotConfig?.language || 'en-GB',
    currency: flow.chatbotConfig?.currency || 'GBP',
    units: flow.chatbotConfig?.units || 'metric',
    defaultRegion: flow.chatbotConfig?.defaultRegion || 'UK',
  },
  questionFlow: (flow.questionFlow || flow.questions || []).map((q: any) => ({
    questionId: q.questionId,
    step: q.step,
    category: q.category,
    categoryName: q.categoryName || '',
    optionType: q.optionType || 0,
    optionTypeName: q.optionTypeName || '',
    question: q.question,
    questionType: q.questionType,
    displayCondition: q.displayCondition,
    responsePath: q.responsePath,
    helperText: q.helperText,
    priceConditions: q.priceConditions,
    options: (q.options || []).map((opt: any) => ({
      optionId: opt.optionId || opt.id,
      optionText: opt.optionText || opt.label || opt.name,
      optionType: opt.optionType || 0,
      inputType: typeof opt.inputType === 'number' ? opt.inputType : 5,
      optionTypeName: opt.optionTypeName || '',
      order: opt.order || 0,
      responseValue: opt.responseValue || opt.value || opt.optionText || opt.label || '',
      thumbnail: opt.thumbnail || '',
      vssPrice: opt.vssPrice ?? opt.vssbase ?? 0,
      price: opt.price ?? opt.basePrice ?? 0,
      priceModifier: opt.priceModifier ?? 0,
      description: opt.description || '',
      numberOfFrames: opt.numberOfFrames || 0,
      numberOfSashes: opt.numberOfSashes,
      displayCondition: opt.displayCondition,
      validProducts: opt.validProducts,
      value: opt.value,
      priceConditions: opt.priceConditions,
      priceList: opt.priceList,
      optionUnit: opt.optionUnit,
      windowJson: opt.windowJson,
      withHornsUrl: opt.withHornsUrl,
      withoutHornsUrl: opt.withoutHornsUrl,
    })),
  })),
  validationRules: (flow as any).validationRules || [],
  pricingRules: (flow as any).pricingRules || [],
});

interface AssistantChatbotPreviewProps {
  conversationFlow: ConversationFlow;
  chatbotKey?: number;
}

export const AssistantChatbotPreview: React.FC<AssistantChatbotPreviewProps> = ({
  conversationFlow,
  chatbotKey,
}) => {
  const assistantConfig = toAssistantConfig(conversationFlow);

  return (
    <AssistantProvider key={chatbotKey} initialConfig={assistantConfig}>
      <div className="bg-white rounded-lg border border-gray-100" style={{ minHeight: 600 }}>
        <Chatbot />
      </div>
    </AssistantProvider>
  );
};
